import { Employee, Branch } from '../../types';
import { arabicEmployeeName } from '../../utils/employeeName';
import { LetterLayout, buildLetterHeaderHtml, buildLetterFooterHtml } from '../../components/HR/PersonnelLetterTemplate';

export interface TaeenFormState {
  passportOffice: string;
  vesselId: string;
  flag: string;
  ownerNationality: string;
  fromPort: string;
  currentLocation: string;
  selectedEmployeeIds: string[];
  /** The sign-on batch date this letter covers (yyyy-mm-dd). */
  batchDate?: string;
}

/** A crew-change batch: employees whose latest sign-on shares one date. */
export interface CrewChangeBatch {
  date: string;
  label: string;
  employeeIds: string[];
  /** Vessel (branch) the batch belongs to — set into the form on select. */
  vesselId?: string;
}

export interface TaeenLetterOptions {
  layout?: LetterLayout;
  /** Sign-on date taken from the latest matching crew change ({{CHANGE_DATE}}). */
  changeDate?: string;
}

/** Default editable letter body. Tokens are replaced at generation time. */
export const TAEEN_DEFAULT_BODY = `<div class="date-line">التاريخ: {{DATE}}</div>
<div class="recipient">السيد / مدير قسم جوازات {{PASSPORT_OFFICE}}</div>
<div class="recipient">تحية طيبة وبعد ،،</div>
<div class="body-text">الرجاء من سيادتكم الموافقة علي تعيين عدد ({{COUNT}}) بحرى على الوحدة {{VESSEL_NAME}} التابعة لشركة بترومارين لدعم المشاريع البحرية والمسجلة تحت علم {{FLAG}} وجنسية المالك {{OWNER_NATIONALITY}} القادمة من ميناء {{FROM_PORT}} والمتواجدة حاليا {{CURRENT_LOCATION}}</div>
<div style="margin-bottom:8px;font-weight:bold;">وهم كالاتي :</div>
{{EMPLOYEE_TABLE}}
<div class="footer">
  <div class="thanks">ولسيادتكم جزيل الشكر</div>
  <div class="sign">مقدمة لسيادتكم توكيل سيلفرسي</div>
</div>`;

export function buildLetterHtml(
  form: TaeenFormState,
  vessel: Branch | undefined,
  selectedEmployees: Employee[],
  templateContent?: string,
  options?: TaeenLetterOptions
): string {
  const today = new Date().toLocaleDateString('ar-EG', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const cellStyle = 'border:1px solid #000;padding:6px 5px;text-align:center;font-size:12px;word-wrap:break-word;overflow-wrap:break-word;';
  // dd/mm/yyyy in Latin digits as an LTR token so the RTL layout can neither
  // reorder it nor push it outside the printable area.
  const birthDate = (value?: string) =>
    value ? `<span dir="ltr">${new Date(value).toLocaleDateString('en-GB')}</span>` : '';

  // Column order is written for dir="rtl": the first <td> renders
  // rightmost, so م (serial) leads on the right like official letters.
  const rows = selectedEmployees
    .map(
      (emp, i) => `
      <tr>
        <td style="${cellStyle}">${i + 1}</td>
        <td style="${cellStyle}">${arabicEmployeeName(emp)}</td>
        <td style="${cellStyle}">${emp.position || ''}</td>
        <td style="${cellStyle}">${emp.seamanBookNumber || ''}</td>
        <td style="${cellStyle}">${birthDate(emp.birthDate)}</td>
        <td style="${cellStyle}">${emp.address || ''}</td>
      </tr>`
    )
    .join('');

  const vesselName = vessel?.name || '';

  const employeeTable = `<table>
    <colgroup>
      <col style="width:5%">
      <col style="width:24%">
      <col style="width:12%">
      <col style="width:15%">
      <col style="width:14%">
      <col style="width:30%">
    </colgroup>
    <thead>
      <tr>
        <th>م</th>
        <th>الاسم</th>
        <th>الوظيفة</th>
        <th>رقم الجواز البحري</th>
        <th>تاريخ الميلاد</th>
        <th>العنوان</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
    </tbody>
  </table>`;

  const employeeNames = selectedEmployees
    .map((emp, i) => `<div>${i + 1}. ${arabicEmployeeName(emp)}</div>`)
    .join('');

  const changeDate = options?.changeDate
    ? new Date(options.changeDate).toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' })
    : today;

  // Use the admin-edited template body when it carries tokens; otherwise
  // fall back to the built-in default wording.
  const rawBody = templateContent && templateContent.includes('{{') ? templateContent : TAEEN_DEFAULT_BODY;
  let body = rawBody
    .replace(/{{DATE}}/g, today)
    .replace(/{{CHANGE_DATE}}/g, changeDate)
    .replace(/{{COMPANY_NAME}}/g, 'شركة بترومارين لدعم المشاريع البحرية')
    .replace(/{{PASSPORT_OFFICE}}/g, form.passportOffice || '')
    .replace(/{{VESSEL_NAME}}/g, vesselName)
    .replace(/{{FLAG}}/g, form.flag || '')
    .replace(/{{OWNER_NATIONALITY}}/g, form.ownerNationality || '')
    .replace(/{{FROM_PORT}}/g, form.fromPort || '')
    .replace(/{{CURRENT_LOCATION}}/g, form.currentLocation || '')
    .replace(/{{COUNT}}/g, String(selectedEmployees.length))
    .replace(/{{EMPLOYEE_NAMES}}/g, employeeNames);
  body = body.includes('{{EMPLOYEE_TABLE}}')
    ? body.replace(/{{EMPLOYEE_TABLE}}/g, employeeTable)
    : body + employeeTable;

  const hasFooter = !!options?.layout?.footerHtml?.trim();

  return `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="UTF-8"/>
  <title>طلب تعيين بحارة</title>
  <style>
    @media print { body { margin: 0; ${hasFooter ? 'padding-bottom: 90px;' : ''} } }
    body {
      font-family: 'Traditional Arabic', 'Arial', sans-serif;
      direction: rtl;
      text-align: right;
      padding: 40px;
      ${hasFooter ? 'padding-bottom: 90px;' : ''}
      line-height: 2;
      font-size: 16px;
      color: #000;
    }
    .date-line { text-align: left; margin-bottom: 16px; }
    .recipient { margin-bottom: 20px; font-weight: bold; font-size: 17px; }
    .body-text { margin-bottom: 20px; text-indent: 2em; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; table-layout: fixed; }
    th { border: 1px solid #000; padding: 6px 5px; background: #e5e7eb; font-weight: bold; text-align: center; font-size: 12px; word-wrap: break-word; }
    td { border: 1px solid #000; padding: 6px 5px; text-align: center; font-size: 12px; word-wrap: break-word; }
    .footer { margin-top: 36px; }
    .footer .thanks { text-align: center; font-weight: bold; margin-bottom: 30px; }
    .footer .sign { text-align: center; }
  </style>
</head>
<body>
  ${buildLetterHeaderHtml(options?.layout, '')}
  ${body}
  ${buildLetterFooterHtml(options?.layout, true)}
</body>
</html>`;
}
